import React from 'react';
import { motion } from 'framer-motion';
import { FaLightbulb, FaUsers, FaChartBar, FaHandshake } from 'react-icons/fa';

const reasons = [
  { icon: FaLightbulb, title: 'Personalized Strategy', description: 'Every campaign is custom-tailored to your unique business goals, audience, and industry dynamics.', stat: '100%', label: 'Custom Solutions' },
  { icon: FaUsers, title: 'Expert Team', description: 'Our professionals bring years of combined experience and proven track records across industries.', stat: '3+', label: 'Years Experience' },
  { icon: FaChartBar, title: 'Data-Driven Results', description: 'We leverage advanced analytics and AI-powered insights to optimize every campaign for maximum ROI.', stat: '150%', label: 'Avg ROI Increase' },
  { icon: FaHandshake, title: 'Dedicated Support', description: '24/7 premium support ensures your success at every step with dedicated account managers.', stat: '24/7', label: 'Available Support' },
];

export default function WhyChooseUs() {
  return (
    <section id="why-us" className="py-32 px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} className="text-center mb-20">
          <motion.span whileHover={{ scale: 1.05 }}
            className="inline-block px-6 py-2 bg-gradient-to-r from-orange-500/10 to-cyan-500/10 border border-orange-500/20 rounded-full text-sm font-semibold text-orange-400 mb-6 backdrop-blur-sm">
            Why Choose Us
          </motion.span>
          <h2 className="text-5xl md:text-7xl font-black mb-6">
            Your Success <span className="bg-gradient-to-r from-orange-400 to-cyan-500 bg-clip-text text-transparent">Partner</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            We're not just another agency — we're your committed growth partner invested in your long-term success
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {reasons.map((r, i) => (
            <motion.div key={r.title}
              initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1, type: 'spring', stiffness: 100 }}
              whileHover={{ y: -12, scale: 1.02 }} className="relative group">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 to-cyan-500/0 group-hover:from-orange-500/10 group-hover:to-cyan-500/10 rounded-3xl transition-all duration-500 blur-xl" />
              <div className="relative bg-gradient-to-br from-gray-50/90 dark:from-gray-900/90 to-white/90 dark:to-black/90 p-10 rounded-3xl border border-gray-200 dark:border-gray-800 group-hover:border-orange-500/40 transition-all duration-500 backdrop-blur-md h-full">
                <motion.div whileHover={{ rotate: 360, scale: 1.1 }} transition={{ duration: 0.8 }} className="mb-8">
                  <div className="w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-2xl relative overflow-hidden">
                    <r.icon className="text-5xl text-gray-900 dark:text-white relative z-10" />
                    <motion.div animate={{ scale: [1, 1.5, 1], opacity: [0.5, 0.8, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity }} className="absolute inset-0 bg-white/20 rounded-2xl" />
                  </div>
                </motion.div>
                <div className="text-center mb-6">
                  <div className="text-5xl font-black bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent mb-2">{r.stat}</div>
                  <p className="text-sm text-gray-500 uppercase tracking-wide font-semibold">{r.label}</p>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-center text-gray-900 dark:text-white group-hover:text-orange-400 transition-colors">{r.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed text-sm">{r.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
